import { useEffect, useMemo, useState } from 'react';
import { auth, db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { recordFormContext } from '../utils/serverFormContext';

/**
 * useRmsFlowContext
 * ------------------
 * Derives { initiatedBy, userRole, formContext } for post-account forms.
 * 
 * Data Sources:
 *   - initiatedBy: Read from patientRecords/{id}.referrerType (set at referral creation
 *     based on relationship_cyp answer: 'guardian' or 'school')
 *   - userRole: Read from auth custom claims (userType: 'RMS Legal Guardian' or 'RMS School Representative';
 *     an 'RMS Teacher' answers as the school, with isTeacher set)
 *
 * The relationship_cyp answer determines both values at account creation time:
 *   - school_rep → referrerType: 'school', userType: 'RMS School Representative'
 *   - All other relationships → referrerType: 'guardian', userType: 'RMS Legal Guardian'
 */
export default function useRmsFlowContext(orgId, patientRecordId) {
  const [initiatedBy, setInitiatedBy] = useState(null);
  const [userRole, setUserRole] = useState(null);
  // CYP's date of birth from the patient record — used to constrain date pickers
  // (e.g., measurement year dropdowns should not go before birth year)
  const [subjectDob, setSubjectDob] = useState(null);
  // CYP's first name — safety signposting names the actual child rather than
  // "the child" (RMS-314).
  const [subjectFirstName, setSubjectFirstName] = useState(null);
  // The child's pronouns (e.g. 'she_her') for personalised wording that needs
  // verb or possessive agreement.
  const [subjectPronouns, setSubjectPronouns] = useState(null);
  // The child's sex and school phase, which some parent questions are shown
  // by (TDI-709). The server sets the same keys from the record on every save.
  const [recordContext, setRecordContext] = useState({});
  const [isTeacher, setIsTeacher] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let nextInitiatedBy = null;
      let nextUserRole = null;
      let nextSubjectDob = null;
      let nextSubjectFirstName = null;
      let nextSubjectPronouns = null;
      let nextRecordContext = {};
      let nextIsTeacher = false;

      // Read referrerType and subject DOB from patient record
      try {
        if (orgId && patientRecordId) {
          const prRef = doc(db, 'rms_organisations', orgId, 'patientRecords', patientRecordId);
          const snap = await getDoc(prRef);
          if (!cancelled) {
            const data = snap.exists() ? (snap.data() || {}) : {};
            const refType = String(data.referrerType || '').toLowerCase();
            nextInitiatedBy = refType === 'guardian' ? 'guardian' : (refType === 'school' ? 'school' : null);
            // Extract CYP's DOB — stored in subject.dob, subject_dob, or dateOfBirth
            nextSubjectDob = data.subject?.dob || data.subject_dob || data.dateOfBirth || null;
            // Same fallback chain the service-user overview uses.
            nextSubjectFirstName =
              data.subject_first_name || data.firstName || data.subject?.firstName || null;
            nextSubjectPronouns = data.subject?.preferredPronouns || data.pronouns || null;
            nextRecordContext = recordFormContext(data);
          }
        }
      } catch (_) {}
      
      // Read userRole from auth custom claims (set based on relationship_cyp at account creation)
      try {
        const u = auth.currentUser;
        if (u) {
          const tok = await u.getIdTokenResult(true);
          const userType = String((tok.claims || {}).userType || '').toLowerCase();
          if (userType.includes('guardian')) nextUserRole = 'guardian';
          else if (userType.includes('school')) nextUserRole = 'school_user';
          else if (userType.includes('teacher')) {
            // A class teacher fills only the school forms their SENCO
            // allocated (TDI-489); the server grants exactly those
            // `<formKey>_school_user` instances, so they answer as the school.
            nextUserRole = 'school_user';
            nextIsTeacher = true;
          }
        }
      } catch (_) {}
      
      if (!cancelled) {
        if (nextInitiatedBy) setInitiatedBy(nextInitiatedBy);
        if (nextUserRole) setUserRole(nextUserRole);
        if (nextSubjectDob) setSubjectDob(nextSubjectDob);
        if (nextSubjectFirstName) setSubjectFirstName(nextSubjectFirstName);
        if (nextSubjectPronouns) setSubjectPronouns(nextSubjectPronouns);
        setRecordContext(nextRecordContext);
        setIsTeacher(nextIsTeacher);
        setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, patientRecordId]);

  const formContext = useMemo(() => ({
    pathway: 'cyp',
    initiatedBy: initiatedBy || undefined,
    currentUser: { role: userRole || undefined },
    // CYP's date of birth (ISO string, e.g. "2018-07-05") — available for date
    // pickers in physical health forms to constrain year ranges to the child's lifetime.
    subjectDob: subjectDob || undefined,
    subjectSex: recordContext.subjectSex || undefined,
    schoolPhase: recordContext.schoolPhase || undefined,
  }), [initiatedBy, userRole, subjectDob, recordContext]);

  return { initiatedBy, userRole, isTeacher, subjectFirstName, subjectPronouns, formContext, ready };
}


